/**
 * Summary Export — Export workflow summaries to Markdown, HTML, and plain text.
 *
 * Converts a structured summary (with objective, methods, results, keyFindings)
 * and execution metadata into downloadable or clipboard-ready formats.
 * All exports run entirely client-side with no server dependencies (see design D5).
 *
 * @module gibbs/workflow/summaryExport
 */

// ─── Format Helpers ────────────────────────────────────────────────────────

/**
 * Format duration in seconds to a human-readable string.
 * @param {number} seconds
 * @returns {string}
 */
function formatDuration(seconds) {
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}m ${secs}s`;
}

/**
 * Format an ISO date string to a readable date.
 * @param {string} isoDate
 * @returns {string}
 */
function formatDate(isoDate) {
    try {
        return new Date(isoDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return isoDate;
    }
}

// ─── Markdown Export ───────────────────────────────────────────────────────

/**
 * Export summary as Markdown with YAML frontmatter.
 *
 * The frontmatter includes date, workflow name, duration, and step count.
 * The body contains the raw summary content.
 *
 * @param {Object} summary - Summary with objective, methods, results, keyFindings, raw
 * @param {Object} metadata - Execution metadata (date, workflowName, duration, stepCount, etc.)
 * @returns {string} Markdown string with YAML frontmatter
 */
export function toMarkdown(summary, metadata) {
    const frontmatter = [
        '---',
        `date: "${metadata.date}"`,
        `workflow: "${metadata.workflowName}"`,
        `duration: "${formatDuration(metadata.duration)}"`,
        `steps: ${metadata.stepCount}`,
        `steps_completed: ${metadata.stepsCompleted}`,
        `steps_failed: ${metadata.stepsFailed}`,
        '---',
    ].join('\n');

    return `${frontmatter}\n\n${summary.raw}\n`;
}

// ─── HTML Export ───────────────────────────────────────────────────────────

/**
 * Export summary as a self-contained HTML document with inline styles.
 *
 * Produces a complete HTML page with a metadata header block, styled section
 * headers, and clean sans-serif typography. No external dependencies.
 *
 * @param {Object} summary - Summary with objective, methods, results, keyFindings, raw
 * @param {Object} metadata - Execution metadata (date, workflowName, duration, stepCount, etc.)
 * @returns {string} Complete HTML document string
 */
export function toHTML(summary, metadata) {
    const readableDate = formatDate(metadata.date);
    const readableDuration = formatDuration(metadata.duration);

    const sections = [
        { title: 'Objective', content: summary.objective },
        { title: 'Methods', content: summary.methods },
        { title: 'Results', content: summary.results },
        { title: 'Key Findings', content: summary.keyFindings },
    ];

    const sectionHTML = sections
        .filter((s) => s.content)
        .map((s) => `        <section>
            <h2>${escapeHTML(s.title)}</h2>
            <p>${escapeHTML(s.content)}</p>
        </section>`)
        .join('\n');

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHTML(metadata.workflowName)} — Workflow Summary</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            max-width: 720px;
            margin: 40px auto;
            padding: 0 20px;
            color: #1a1a1a;
            line-height: 1.6;
        }
        .metadata {
            background: #f5f7fa;
            border: 1px solid #e1e5ea;
            border-radius: 6px;
            padding: 16px 20px;
            margin-bottom: 32px;
        }
        .metadata h1 {
            margin: 0 0 12px 0;
            font-size: 1.4em;
            color: #2c3e50;
        }
        .metadata dl {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 4px 16px;
            margin: 0;
            font-size: 0.9em;
        }
        .metadata dt {
            font-weight: 600;
            color: #555;
        }
        .metadata dd {
            margin: 0;
        }
        h2 {
            color: #2c3e50;
            border-bottom: 2px solid #e1e5ea;
            padding-bottom: 6px;
            margin-top: 28px;
        }
        section p {
            margin: 8px 0 0 0;
            white-space: pre-wrap;
        }
    </style>
</head>
<body>
    <div class="metadata">
        <h1>${escapeHTML(metadata.workflowName)}</h1>
        <dl>
            <dt>Date</dt><dd>${escapeHTML(readableDate)}</dd>
            <dt>Duration</dt><dd>${escapeHTML(readableDuration)}</dd>
            <dt>Steps</dt><dd>${metadata.stepsCompleted} of ${metadata.stepCount} completed${metadata.stepsFailed > 0 ? `, ${metadata.stepsFailed} failed` : ''}</dd>
        </dl>
    </div>
${sectionHTML}
</body>
</html>
`;
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str
 * @returns {string}
 */
function escapeHTML(str) {
    if (typeof str !== 'string') return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// ─── Plain Text Export ─────────────────────────────────────────────────────

/**
 * Export summary as plain text with section headers and metadata.
 *
 * Section headers are uppercase labels. No markup is used.
 *
 * @param {Object} summary - Summary with objective, methods, results, keyFindings, raw
 * @param {Object} metadata - Execution metadata (date, workflowName, duration, stepCount, etc.)
 * @returns {string} Plain text string
 */
export function toPlainText(summary, metadata) {
    const readableDate = formatDate(metadata.date);
    const readableDuration = formatDuration(metadata.duration);

    const header = [
        metadata.workflowName,
        '='.repeat(metadata.workflowName.length),
        `Date: ${readableDate}`,
        `Duration: ${readableDuration}`,
        `Steps: ${metadata.stepsCompleted} of ${metadata.stepCount} completed${metadata.stepsFailed > 0 ? `, ${metadata.stepsFailed} failed` : ''}`,
        '',
    ].join('\n');

    const sections = [
        { title: 'OBJECTIVE', content: summary.objective },
        { title: 'METHODS', content: summary.methods },
        { title: 'RESULTS', content: summary.results },
        { title: 'KEY FINDINGS', content: summary.keyFindings },
    ];

    const body = sections
        .filter((s) => s.content)
        .map((s) => `${s.title}\n${'-'.repeat(s.title.length)}\n${s.content}`)
        .join('\n\n');

    return `${header}\n${body}\n`;
}

// ─── Browser Download ──────────────────────────────────────────────────────

/**
 * Trigger a browser file download via Blob URL.
 *
 * Creates a Blob from the content, generates an object URL, creates a hidden
 * anchor element, triggers a click to start the download, then revokes the URL.
 *
 * @param {string} content - File content to download
 * @param {string} filename - Suggested filename (e.g. "summary.md")
 * @param {string} mimeType - MIME type (e.g. "text/markdown")
 */
export function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();

    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

// ─── Clipboard ─────────────────────────────────────────────────────────────

/**
 * Copy text to the system clipboard.
 *
 * Uses the modern navigator.clipboard API with a fallback to
 * document.execCommand('copy') for older browsers.
 *
 * @param {string} text - Text to copy to clipboard
 * @returns {Promise<boolean>} true if copy succeeded, false otherwise
 */
export async function copyToClipboard(text) {
    // Modern API
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fall through to legacy fallback
        }
    }

    // Legacy fallback
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        return success;
    } catch {
        return false;
    }
}
